import "amazon-connect-streams";
import { getLogger } from "@expert/logging";

const logger = getLogger({ module: "chatContactConnectionRegistry" });

/**
 * ## Contact Connection Registry
 *
 * Manages active `connect.AgentChatSession` connections with automatic memory management.
 *
 * **Storage:**
 * - `WeakMap<Contact, AgentChatSession>` - Allows automatic garbage collection of Contact objects
 *
 * **Cleanup:**
 * - Automatic: Entries are GC'd when Contact objects are no longer referenced
 * - Explicit: `removeChatContactConnection()` for immediate cleanup
 */
const connectionRegistry = new WeakMap<connect.Contact, connect.AgentChatSession>();

/**
 * Retrieves a chat connection associated with the given Contact.
 * Returns undefined if the connection doesn't exist or was already garbage collected.
 */
export function getChatContactConnection(contact: connect.Contact): connect.AgentChatSession | undefined {
    return connectionRegistry.get(contact);
}

/**
 * Stores a new chat connection in the registry.
 */
export function setChatContactConnection(contact: connect.Contact, connection: connect.AgentChatSession): void {
    logger.debug({ contactId: contact.contactId }, "Caching new ChatJS connection in registry.");
    connectionRegistry.set(contact, connection);
}

/**
 * Removes a chat connection from the registry.
 * Safe to call multiple times - no-op if connection is already removed.
 */
export function removeChatContactConnection(contact: connect.Contact): void {
    const existed = connectionRegistry.has(contact);
    if (existed) {
        logger.debug({ contactId: contact.contactId }, "Removing ChatJS connection from registry.");
        connectionRegistry.delete(contact);
    }
}
